import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Loader2, Send, Save, BellRing, Clock, Globe2, CheckCircle2, AlertTriangle } from "lucide-react";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const normalize = (p) => {
  if (!["off", "daily", "weekly"].includes(p?.effective_email_frequency)) {
    throw new Error("Email controls require the updated server. No email preference has been confirmed saved.");
  }
  return { ...p, email_frequency: p.effective_email_frequency, email_weekday: p.email_weekday ?? 0 };
};

const COMMON_TZS = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Vancouver", "Europe/London", "Europe/Paris",
  "Europe/Berlin", "Europe/Madrid", "Asia/Tokyo", "Asia/Singapore",
  "Asia/Kolkata", "Australia/Sydney", "UTC",
];

export default function NotificationPreferencesPage() {
  const { user } = useAuth();
  if (!user?.id) return null;
  return <AccountNotificationPreferences key={`${user.id}:${user.company_id}`} userId={user.id} />;
}

function AccountNotificationPreferences({ userId }) {
  // A different browser tab can replace the cookie. Bind each preference
  // request to the displayed account; the backend rejects any mismatch.
  const requestConfig = useMemo(() => ({ headers: { "X-ShelfLife-Outbox-User": userId } }), [userId]);
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState(null);
  const [dirty, setDirty] = useState(false);
  const edit = (next) => { setPrefs(next); setDirty(true); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notification-preferences", requestConfig);
      setPrefs(normalize(data.preferences));
      setPolicy(data.admin_policy);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }, [requestConfig]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const { data } = await api.put("/notification-preferences", {
        digest_enabled: prefs.digest_enabled,
        digest_method: prefs.digest_method,
        digest_time_hhmm: prefs.digest_time_hhmm,
        timezone: prefs.timezone,
        email_frequency: prefs.email_frequency,
        email_weekday: prefs.email_weekday,
      }, requestConfig);
      setPrefs(normalize(data.preferences));
      setDirty(false);
      setLastTest(null);
      setPolicy(data.admin_policy);
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setLastTest(null);
    try {
      const { data } = await api.post("/notification-preferences/test-digest", undefined, requestConfig);
      setLastTest(data);
      if (data.ok) {
        toast.success(`Test digest sent — ${data.item_count} item${data.item_count === 1 ? "" : "s"}`);
      } else {
        toast.warning(`Test digest attempt logged — ${data.error || "delivery failed"}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center" data-testid="notification-preferences-loading">
        <Loader2 className="animate-spin mx-auto text-ink/40" size={28} />
      </div>
    );
  }
  if (!prefs || !policy) return <div role="alert">Unable to load notification settings. <button onClick={load}>Try again</button></div>;

  const digestLocked = policy.required_daily_digest;

  return (
    <div className="max-w-3xl" data-testid="notification-preferences-page">
      <div className="data-sku mb-2">Account</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-none mb-2">
        Notification Preferences
      </h1>
      <p className="text-sm text-ink/60 mb-6 max-w-2xl">
        Control emails for your account only. Choose Off, Daily, or Weekly. Turning email off
        does not turn off in-app alerts, password resets, or account invitations.
      </p>

      <fieldset disabled={saving || testing} className="min-w-0">

      {/* Daily digest toggle */}
      <section className="card-flat p-4 sm:p-5 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <BellRing size={18} />
          <h2 className="font-display font-black text-lg tracking-tight">In-app digest</h2>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={digestLocked || (prefs.digest_enabled && ["in_app", "both"].includes(prefs.digest_method))}
            disabled={digestLocked}
            onChange={(e) => edit({ ...prefs, digest_enabled: e.target.checked, digest_method: e.target.checked ? "in_app" : "email" })}
            className="mt-1 h-5 w-5 accent-ink"
            data-testid="digest-enabled-checkbox"
          />
          <div className="flex-1">
            <div className="font-display font-semibold text-sm">
              Show a daily summary inside ShelfLife
            </div>
            <div className="text-xs text-ink/60 mt-0.5">
              A list of inventory items grouped by department, broken down into EXPIRED / TODAY / 3-DAY / 7-DAY.
            </div>
            {digestLocked && (
              <div className="mt-2 text-xs text-amber-700 flex items-center gap-1.5" data-testid="digest-locked-hint">
                <AlertTriangle size={12} /> Your admin requires an in-app summary. You can still turn email off or choose weekly emails.
              </div>
            )}
          </div>
        </label>
      </section>

      {/* Delivery + timing */}
      <section className="card-flat p-4 sm:p-5 mb-4 overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="min-w-0">
            <label htmlFor="email-frequency" className="data-sku mb-1 flex items-center gap-1"><Send size={12}/> Email updates</label>
            <select
              id="email-frequency"
              value={prefs.email_frequency}
              onChange={(e) => edit({ ...prefs, email_frequency: e.target.value })}
              className="block w-full max-w-full h-11 px-3 border border-border rounded-sm bg-white font-display text-sm appearance-none"
              data-testid="email-frequency-select"
            >
              <option value="off">Off — no notification emails</option>
              <option value="daily">Daily summary</option>
              <option value="weekly">Weekly summary</option>
            </select>
            {prefs.email_frequency === "weekly" && <div className="mt-3">
              <label htmlFor="email-weekday" className="data-sku">Send every</label>
              <select id="email-weekday" className="block w-full h-11 px-3 border border-border bg-white" value={prefs.email_weekday} onChange={(e) => edit({ ...prefs, email_weekday: Number(e.target.value) })}>
                {WEEKDAYS.map((day, i) => <option key={day} value={i}>{day}</option>)}
              </select>
            </div>}
            <p className="mt-2 text-xs text-ink/60">A snapshot of expiring and expired stock at the scheduled time, not a separate email per item.</p>
          </div>
          <div className="min-w-0">
            <div className="data-sku mb-1 flex items-center gap-1"><Clock size={12}/> Digest time</div>
            <div className="flex items-center gap-2" data-testid="digest-time-picker">
              <select
                value={prefs.digest_time_hhmm.split(":")[0]}
                onChange={(e) => {
                  const hh = e.target.value;
                  const mm = (prefs.digest_time_hhmm.split(":")[1] || "00");
                  edit({ ...prefs, digest_time_hhmm: `${hh}:${mm}` });
                }}
                className="block w-full max-w-full h-11 px-3 border border-border rounded-sm bg-white font-mono text-sm text-center appearance-none"
                data-testid="digest-time-input"
                aria-label="Digest hour"
              >
                {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              <span className="font-mono text-ink/50 text-sm select-none">:</span>
              <select
                value={prefs.digest_time_hhmm.split(":")[1] || "00"}
                onChange={(e) => {
                  const mm = e.target.value;
                  const hh = (prefs.digest_time_hhmm.split(":")[0] || "07");
                  edit({ ...prefs, digest_time_hhmm: `${hh}:${mm}` });
                }}
                className="block w-full max-w-full h-11 px-3 border border-border rounded-sm bg-white font-mono text-sm text-center appearance-none"
                data-testid="digest-time-minute-input"
                aria-label="Digest minute"
              >
                {["00", "15", "30", "45"].map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="min-w-0">
            <div className="data-sku mb-1 flex items-center gap-1"><Globe2 size={12}/> Timezone</div>
            <select
              value={prefs.timezone}
              onChange={(e) => edit({ ...prefs, timezone: e.target.value })}
              className="block w-full max-w-full h-11 px-3 border border-border rounded-sm bg-white font-mono text-sm appearance-none"
              data-testid="digest-timezone-select"
            >
              {COMMON_TZS.includes(prefs.timezone) ? null : <option value={prefs.timezone}>{prefs.timezone}</option>}
              {COMMON_TZS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap gap-3 mb-4">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary h-11"
          data-testid="save-preferences-button"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
          {saving ? "Saving…" : "Save preferences"}
        </button>
        <button
          onClick={sendTest}
          disabled={testing || dirty || (prefs.email_frequency === "off" && !digestLocked && !(prefs.digest_enabled && ["in_app", "both"].includes(prefs.digest_method)))}
          className="btn-secondary h-11"
          data-testid="send-test-digest-button"
        >
          {testing ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
          {testing ? "Sending test…" : "Send test digest now"}
        </button>
      </div>
      {dirty && <p className="text-sm mb-4" role="status">Unsaved changes — select Save preferences to apply them.</p>}
      </fieldset>

      {lastTest && (
        <section className="card-flat p-4 sm:p-5 mb-10" data-testid="test-digest-result">
          <div className={`flex items-center gap-2 font-display font-bold mb-3 ${lastTest.ok ? "text-emerald-700" : "text-amber-700"}`}>
            {lastTest.ok ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
            {lastTest.ok ? "Test digest sent" : "Test digest attempted"}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><div className="data-sku">Items</div><div className="font-mono">{lastTest.item_count}</div></div>
            <div><div className="data-sku">Departments</div><div className="font-mono">{lastTest.departments?.length || 0}</div></div>
            <div><div className="data-sku">Email</div><div className="font-mono">{lastTest.delivered_email ? "sent" : "—"}</div></div>
            <div><div className="data-sku">In-app</div><div className="font-mono">{lastTest.delivered_in_app ? "sent" : "—"}</div></div>
          </div>
          {lastTest.error && (
            <div className="mt-3 text-xs text-rose-700 font-mono" data-testid="test-digest-error">{lastTest.error}</div>
          )}
        </section>
      )}
    </div>
  );
}
